import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography, IconButton } from "@mui/material";
import {
    CampaignOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    FlagOutlined,
    PersonAddOutlined,
    SportsEsportsOutlined,
    VerifiedUserOutlined,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";

const MotionBox = motion(Box);

const ICON_SX = {
    fontSize: "1.15rem",
    color: colors.primary.main,
};

function getNotificationIcon(type) {
    switch (type) {
        case "friend_request":
            return <PersonAddOutlined sx={ICON_SX} />;
        case "friend_accepted":
            return (
                <CheckCircleOutlined
                    sx={{ ...ICON_SX, color: colors.state.success.main }}
                />
            );
        case "match_result":
            return <SportsEsportsOutlined sx={ICON_SX} />;
        case "reputation_change":
            return <VerifiedUserOutlined sx={ICON_SX} />;
        case "report_status":
            return <FlagOutlined sx={ICON_SX} />;
        case "system_announcement":
        default:
            return <CampaignOutlined sx={ICON_SX} />;
    }
}

export default function NotificationItem({
    notification,
    onMarkRead,
    onDelete,
    onNavigate,
}) {
    const appealUrl = notification?.metadata?.appealUrl || null;

    const navigateToUrl = (url) => {
        if (!url) {
            return;
        }

        if (!notification.read && onMarkRead) {
            onMarkRead(notification._id);
        }
        if (onNavigate) {
            onNavigate(url);
        }
    };

    const handleClick = () => {
        if (notification.actionUrl) {
            navigateToUrl(notification.actionUrl);
            return;
        }
        if (!notification.read && onMarkRead) {
            onMarkRead(notification._id);
        }
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        if (onDelete) {
            onDelete(notification._id);
        }
    };

    const handleAppealClick = (e) => {
        e.stopPropagation();
        navigateToUrl(appealUrl);
    };

    const timeAgo = notification.createdAt
        ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
        : "";

    return (
        <MotionBox
            className="notificationItem"
            onClick={handleClick}
            initial={false}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            sx={{
                position: "relative",
                display: "flex",
                alignItems: "flex-start",
                gap: "0.7rem",
                px: "0.65rem",
                py: "0.7rem",
                borderRadius: "0.55rem",
                cursor: notification.actionUrl ? "pointer" : "default",
                transition: "background 180ms ease",
                "&:hover": {
                    background: `
                        linear-gradient(
                            90deg,
                            rgba(255,255,255,0.06),
                            rgba(255,255,255,0.03)
                        )
                    `,
                },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "1.25rem",
                    height: "1.25rem",
                    mt: "0.08rem",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {getNotificationIcon(notification.type)}
                {!notification.read ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "-0.12rem",
                            right: "-0.18rem",
                            width: "0.38rem",
                            height: "0.38rem",
                            borderRadius: "50%",
                            backgroundColor: colors.primary.main,
                        }}
                    />
                ) : null}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: "0.85rem",
                        fontWeight: notification.read ? 500 : 600,
                        color: colors.text.primary,
                        lineHeight: 1.4,
                        letterSpacing: "0.01em",
                    }}
                >
                    {notification.message}
                </Typography>
                <Typography
                    sx={{
                        mt: "0.22rem",
                        fontSize: "0.68rem",
                        fontWeight: 500,
                        letterSpacing: "0.02em",
                        color: colors.text.muted,
                    }}
                >
                    {timeAgo}
                </Typography>
                {appealUrl ? (
                    <Button
                        size="small"
                        onClick={handleAppealClick}
                        sx={{
                            mt: "0.4rem",
                            px: 0,
                            minWidth: 0,
                            color: colors.primary.main,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            textTransform: "none",
                            justifyContent: "flex-start",
                            "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.accent.main,
                            },
                        }}
                    >
                        Appeal this outcome
                    </Button>
                ) : null}
            </Box>

            <IconButton
                onClick={handleDelete}
                size="small"
                aria-label="Dismiss notification"
                sx={{
                    width: "1.55rem",
                    height: "1.55rem",
                    mt: "-0.1rem",
                    mr: "-0.2rem",
                    color: colors.text.muted,
                    "&:hover": {
                        color: colors.state.danger.main,
                        background: alpha(colors.state.danger.main, 0.1),
                    },
                }}
            >
                <CloseOutlined sx={{ fontSize: "0.95rem" }} />
            </IconButton>
        </MotionBox>
    );
}

NotificationItem.propTypes = {
    notification: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired,
        actionUrl: PropTypes.string,
        metadata: PropTypes.shape({
            appealUrl: PropTypes.string,
        }),
        read: PropTypes.bool.isRequired,
        createdAt: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date),
        ]),
    }).isRequired,
    index: PropTypes.number,
    onMarkRead: PropTypes.func,
    onDelete: PropTypes.func,
    onNavigate: PropTypes.func,
};
